import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import Packages from "@/components/Packages";
import LeadForm from "@/components/LeadForm";
import { tourismCategories, tourismList } from "@/data/tourism";
import { findCaseStudyFor, caseStudies } from "@/data/caseStudies";
import { videoAdsByTrade, displayCampaignsByTrade } from "@/data/reel";
import {
  DMOCapabilitiesSection,
  DMOProcurementSection,
  DMOVinesCallout,
} from "@/components/DMOExtras";
import ScrollingReference from "@/components/ScrollingReference";
import NelsonTrilogyStrip from "@/components/NelsonTrilogyStrip";
import WineriesVinesSection from "@/components/WineriesVinesSection";
import WineryListingTiers from "@/components/WineryListingTiers";
import {
  Globe,
  Wine,
  BedDouble,
  Ticket,
  Utensils,
  CalendarHeart,
  AlertTriangle,
  Calendar,
  Quote,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Sparkles,
  MapPin,
  Film,
} from "lucide-react";

const ICONS = { Globe, Wine, BedDouble, Ticket, Utensils, CalendarHeart, Sparkles };

const HIGHLIGHT_WORDS = new Set([
  "visitors",
  "impact",
  "funding",
  "remember",
  "remember.",
  "stop",
  "next",
  "stop.",
  "trail",
  "itinerary.",
  "rooms",
  "rooms.",
  "otas",
  "booking",
  "booking.",
  "weekend",
  "weekend.",
  "ticket",
  "tickets",
  "sales",
  "reservation",
  "home",
  "home.",
  "event",
  "next",
  "one",
  "one.",
  "discovered",
  "bookings",
  "reach",
]);

const TourismCategoryPage = () => {
  const { slug } = useParams();
  const category = tourismCategories[slug];

  if (!category) return <Navigate to="/tourism" replace />;

  const Icon = ICONS[category.icon] || Globe;
  const otherCategories = tourismList
    .filter((c) => c.slug !== category.slug)
    .slice(0, 3);
  const relevantCaseStudy = findCaseStudyFor(category.slug);
  const categoryAds = videoAdsByTrade(category.slug);
  const categoryCampaigns = displayCampaignsByTrade(category.slug);
  const hasAnyCategoryAds = categoryAds.length > 0 || categoryCampaigns.length > 0;

  const headlineWords = category.heroHeadline.split(" ");

  return (
    <SiteShell>
      {/* WINERIES-only top ribbon: vines.app integration callout */}
      {category.slug === "wineries-breweries" && (
        <div
          className="bg-[var(--af-lime)] text-[var(--af-navy)]"
          data-testid="wineries-vines-ribbon"
        >
          <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
            <div className="flex items-center gap-3">
              <MapPin size={18} strokeWidth={2.2} className="flex-shrink-0" />
              <div className="font-semibold leading-snug">
                Now powering{" "}
                <span className="font-display tracking-tight">280+</span>{" "}
                Virginia wineries via{" "}
                <a
                  href="https://vines.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:no-underline"
                >
                  vines.app
                </a>{" "}
               , featured placement available.
              </div>
            </div>
            <div className="flex gap-3 sm:flex-shrink-0">
              <a
                href="#winery-listing-tiers-section"
                className="font-semibold text-xs uppercase tracking-wider hover:underline underline-offset-2 inline-flex items-center gap-1"
                data-testid="wineries-vines-ribbon-cta-tiers"
              >
                See listing tiers <ArrowRight size={14} />
              </a>
              <span className="text-[var(--af-navy)]/40">·</span>
              <Link
                to="/platforms"
                className="font-semibold text-xs uppercase tracking-wider hover:underline underline-offset-2"
                data-testid="wineries-vines-ribbon-cta-platforms"
              >
                Platforms
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="category-hero"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-10 pb-24 sm:pt-14 sm:pb-28 relative z-10">
          <Link
            to="/tourism"
            className="inline-flex items-center gap-2 text-xs text-[var(--af-cream)]/55 hover:text-[var(--af-lime)] mb-10 transition-colors"
            data-testid="category-back-link"
          >
            <ArrowLeft size={14} /> All Tourism Categories
          </Link>

          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7 fade-up">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-12 h-12 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center">
                  <Icon size={22} className="text-[var(--af-lime)]" />
                </div>
                <div className="eyebrow">{category.heroEyebrow}</div>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7">
                {headlineWords.map((w, i) => {
                  const lower = w.toLowerCase().replace(/[.,!?]/g, "");
                  const isHl = HIGHLIGHT_WORDS.has(lower);
                  return (
                    <React.Fragment key={i}>
                      <span className={isHl ? "text-[var(--af-lime)]" : undefined}>
                        {w}
                      </span>{" "}
                    </React.Fragment>
                  );
                })}
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-8 leading-relaxed">
                {category.heroSub}
              </p>
              {category.slug === "wineries-breweries" && (
                <div
                  className="flex items-center gap-4 mb-7 flex-wrap"
                  data-testid="wineries-hero-vifl-row"
                >
                  <img
                    src="/media/clients/vifl-horizontal.png"
                    alt="Virginia Is For Lovers"
                    className="h-7 sm:h-8 w-auto opacity-95 flex-shrink-0"
                    data-testid="wineries-hero-vifl-logo"
                  />
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[var(--af-lime)]/40 bg-[var(--af-lime)]/10">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                    <span className="font-eyebrow text-[11px] uppercase tracking-widest text-[var(--af-lime)]">
                      VTC Grant-Aligned · For Wineries, Breweries, Cideries
                    </span>
                  </div>
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <a
                  href="#packages"
                  className="btn-lime"
                  data-testid="category-hero-cta-packages"
                >
                  See the 90-Day Packages <ArrowRight size={14} />
                </a>
                <a
                  href="#lead-form"
                  className="btn-ghost"
                  data-testid="category-hero-cta-audit"
                >
                  Free {category.shortName} Audit
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 lg:mt-[68px]">
              {category.slug === "dmos" ? (
                <ScrollingReference />
              ) : (
                <div
                  className="surface p-7 fade-up overflow-hidden"
                  style={{ animationDelay: ".15s" }}
                >
                  {category.slug === "wineries-breweries" && (
                    <div
                      className="rounded-xl overflow-hidden border border-[var(--af-line)] mb-6 aspect-video bg-black -mx-2 -mt-2"
                      data-testid="wineries-hero-video-wrap"
                    >
                      <video
                        src="/media/reel/stinson-loop.mp4"
                        poster="/media/reel/stinson.jpg"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        onLoadedMetadata={(e) => {
                          e.currentTarget.play().catch(() => {});
                        }}
                        onEnded={(e) => {
                          // Belt-and-suspenders: some browsers occasionally
                          // skip the loop attribute on long-running videos.
                          e.currentTarget.currentTime = 0;
                          e.currentTarget.play().catch(() => {});
                        }}
                        className="w-full h-full object-cover"
                        data-testid="wineries-hero-video"
                      />
                    </div>
                  )}
                  <div className="eyebrow mb-3">
                    Built for {category.shortName}
                  </div>
                  <div className="font-display text-4xl leading-none mb-3">
                    Multi-state.
                  </div>
                  <div className="font-display text-4xl leading-none mb-3 text-[var(--af-lime)]">
                    Omni-channel.
                  </div>
                  <div className="font-display text-4xl leading-none">
                    Measurable.
                  </div>
                  <p className="text-sm mt-6 text-[var(--af-cream)]/70 leading-relaxed">
                    All packages under $10K. Three tiers. Real reporting every
                    month.
                  </p>
                  <a
                    href="#lead-form"
                    className="mt-5 inline-flex items-center gap-1.5 text-xs text-[var(--af-lime)] hover:underline underline-offset-4 font-medium"
                    data-testid="custom-pricing-link"
                  >
                    Looking for Custom Pricing? <ArrowRight size={12} />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PAIN POINTS — moved to the bottom of the page, just above the
          testimonial. Original location was right after the hero. */}

      {/* Wineries-only: "Why being on vines.app matters" callout, sits
          immediately under the hero so the platform pitch lands first. */}
      {category.slug === "wineries-breweries" && <WineriesVinesSection />}

      {/* DMO-only block. Order:
          1) Verified Public-Sector Reference (already in hero right column)
          2) Featured Work · Nelson County (trilogy)
          3) DMOs · Full-Service Capabilities
          4) The Platform Play (vines.app custom-build callout)
          5) Built for Public-Sector Buying */}
      {category.slug === "dmos" && (
        <>
          <NelsonTrilogyStrip />
          <DMOCapabilitiesSection />
          <DMOVinesCallout />
          <DMOProcurementSection />
        </>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="dashed-divider" />
      </div>

      {/* SEASONAL TRIGGERS — hidden on DMOs */}
      {category.slug !== "dmos" && (
      <section
        className="px-5 sm:px-8 py-24"
        data-testid="category-seasonal-section"
      >
        <div className="max-w-7xl mx-auto">
          <div className="mb-14 max-w-3xl">
            <div className="eyebrow mb-4">
              {category.shortName} · Seasonal Strategy
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
              Your campaign{" "}
              <span className="text-[var(--af-lime)]">moves</span> with the
              season.
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
              Tourism runs in waves. We build campaigns that
              flex with peak, shoulder, and off-season so every dollar lands
              when it's most likely to convert.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {category.seasonalTriggers.map((s, i) => (
              <div
                key={i}
                className="surface p-6"
                data-testid={`category-seasonal-${i}`}
              >
                <Calendar size={22} className="text-[var(--af-lime)] mb-4" />
                <div className="font-display text-xl mb-2">{s.season}</div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {s.trigger}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* PACKAGES — hidden on DMOs (includes the "Quick Note on Social Spotlight" subnote) */}
      {category.slug !== "dmos" && (
        <Packages vertical="tourism" examples={category.packageExamples} />
      )}

      {/* SAMPLE ADS — Navigational pointer only. Full gallery lives at
          /sample-ads to keep individual category pages light. */}
      <section
        className="px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="category-sample-ads-section"
      >
          <div className="max-w-7xl mx-auto">
            <div className="mb-10 max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <Film size={18} className="text-[var(--af-lime)]" />
                <div className="eyebrow">
                  Sample Ads · {category.shortName}
                </div>
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-4">
                Real {category.shortName} campaigns we&apos;ve{" "}
                <span className="text-[var(--af-lime)]">produced &amp; placed.</span>
              </h2>
              <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
                Mobile display, TV, social, and Reels, built for destination
                marketing and proven against real visitor metrics.
              </p>
            </div>
            <div
              className="surface p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5"
              data-testid="category-sample-ads-cta-card"
            >
              <div>
                <div className="font-eyebrow text-[10px] uppercase tracking-widest text-[var(--af-lime)] mb-2">
                  Open the gallery
                </div>
                <div className="font-display text-xl sm:text-2xl leading-tight">
                  See every {category.shortName} ad we&apos;ve aired,
                  filterable by format.
                </div>
              </div>
              <Link
                to="/sample-ads"
                className="btn-lime whitespace-nowrap"
                data-testid="category-sample-ads-see-all"
              >
                View sample ads <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

      {/* DMO-specific: removed from here — DMOVinesCallout was hoisted up
          to sit right after DMOCapabilitiesSection. */}

      {/* Wineries-specific extras: vines.app tiered-listing offer */}
      {category.slug === "wineries-breweries" && (
        <div
          id="winery-listing-tiers-section"
          className="border-y border-[var(--af-line)] bg-[var(--af-navy-deep)]"
        >
          <WineryListingTiers />
        </div>
      )}

      {/* RELEVANT CASE STUDY CALLOUT */}
      {/* RELEVANT CASE STUDY CALLOUT — hidden on DMOs because the trilogy
          strip above already showcases Wanderlove, Dark Skies, and EDL. */}
      {relevantCaseStudy && category.slug !== "dmos" && (
        <section
          className="px-5 sm:px-8 py-16"
          data-testid="category-case-study-callout"
        >
          <div className="max-w-7xl mx-auto">
            <div className="surface overflow-hidden grid md:grid-cols-12 gap-0">
              <div className="md:col-span-4 relative bg-gradient-to-br from-[var(--af-navy-soft)] to-[var(--af-navy-deep)] min-h-[200px] md:min-h-full overflow-hidden">
                {relevantCaseStudy.videoUrl ? (
                  <video
                    src={relevantCaseStudy.videoUrl}
                    poster={relevantCaseStudy.videoPoster}
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onLoadedMetadata={(e) => {
                      e.currentTarget.play().catch(() => {});
                    }}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="category-case-study-video"
                  />
                ) : (
                  <img
                    src={relevantCaseStudy.image}
                    alt={relevantCaseStudy.client}
                    className="absolute inset-0 w-full h-full object-cover opacity-90"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[var(--af-navy-soft)]/60" />
              </div>
              <div className="md:col-span-8 p-7 sm:p-10">
                <div className="eyebrow mb-3">
                  Proof · {relevantCaseStudy.categoryLabel}
                </div>
                <div className="flex items-baseline gap-4 mb-3">
                  <div className="font-display text-5xl sm:text-6xl text-[var(--af-lime)] leading-none">
                    {relevantCaseStudy.heroMetric.value}
                  </div>
                  <div className="text-sm text-[var(--af-cream)]/65 uppercase tracking-wider">
                    {relevantCaseStudy.heroMetric.label}
                  </div>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl mb-3 leading-tight">
                  {relevantCaseStudy.headline}
                </h3>
                <p className="text-sm text-[var(--af-cream)]/75 mb-6 leading-relaxed max-w-2xl">
                  {relevantCaseStudy.summary}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/tourism/case-studies"
                    className="btn-lime"
                    data-testid="category-case-study-see-all"
                  >
                    See all case studies <ArrowRight size={14} />
                  </Link>
                  <a
                    href={relevantCaseStudy.externalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-ghost"
                    data-testid="category-case-study-external"
                  >
                    Full case study <ArrowUpRight size={14} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* DMO-only second case study (Dark Skies) was hoisted up to right
          after the Procurement section. See block near the pain-points
          section above. */}

      {/* PAIN POINTS — moved to the bottom of the page, sits just above
          the testimonial. Hidden on DMOs. */}
      {category.slug !== "dmos" && (
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="category-pain-section"
      >
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">
              What We Hear From {category.shortName} Marketers
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
              You're not short on{" "}
              <span className="text-[var(--af-lime)]">stories.</span>
              <br />
              You're short on the right{" "}
              <span className="text-[var(--af-lime)]">distribution.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {category.painPoints.map((p, i) => (
              <div
                key={i}
                className="flex gap-4 items-start surface p-5"
                data-testid={`category-pain-${i}`}
              >
                <AlertTriangle
                  size={20}
                  className="text-[var(--af-lime)] mt-0.5 flex-shrink-0"
                />
                <div className="text-[var(--af-cream)]/90 leading-relaxed">
                  {p}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIAL — hidden on DMOs (the Maureen Kelley quote is already
          surfaced in the hero scrolling reference marquee). */}
      {category.testimonial && category.slug !== "dmos" && (
        <section
          className="grid-bg border-t border-b border-[var(--af-line)] px-5 sm:px-8 py-24"
          data-testid="category-testimonial"
        >
          <div className="max-w-4xl mx-auto text-center">
            <Quote size={42} className="text-[var(--af-lime)] mx-auto mb-6" />
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
              "{category.testimonial.quote}"
            </p>
            <div className="eyebrow justify-center inline-flex">
              {category.testimonial.author}
            </div>
          </div>
        </section>
      )}

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="px-5 sm:px-8 py-24"
        data-testid="category-lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          {category.slug === "dmos" ? (
            <div>
              <div className="eyebrow mb-4">DMOs · No Commitment</div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6 leading-tight">
                Set up your{" "}
                <span className="text-[var(--af-lime)]">20-minute</span>{" "}
                strategy call.
              </h2>
              <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
                No pressure, just a peer-to-peer conversation about your
                destination, your peak season, and the KPIs your board
                actually cares about. You bring the questions. We'll bring
                fresh campaign thinking from 15+ years inside Virginia
                tourism.
              </p>
              <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  20 minutes · no slide deck · just ideas
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  Agency-to-DMO, peer-to-peer
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  We'll arrive with 2–3 fresh campaign concepts for your region
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  If we're a fit, we'll talk SOW. If not, the ideas are yours.
                </li>
              </ul>
              <div
                className="mt-7 inline-flex items-center gap-3 px-4 py-2.5 rounded-full border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60"
                data-testid="dmo-form-vifl-badge"
              >
                <img
                  src="/media/clients/vifl-horizontal.png"
                  alt="Virginia Is For Lovers"
                  className="h-5 w-auto opacity-95 flex-shrink-0"
                />
                <div className="text-[11px] uppercase tracking-widest text-[var(--af-cream)]/80 font-medium">
                  Built for VTC grant recipients · VIFL-compliant
                </div>
              </div>
            </div>
          ) : (
            <div>
              <div className="eyebrow mb-4">
                {category.shortName} · Start Here
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
                Want a custom plan
                <br />
                for your{" "}
                <span className="text-[var(--af-lime)]">
                  {category.shortName.toLowerCase()}
                </span>{" "}
                brand?
              </h2>
              <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
                Tell us your destination, your peak season, and your top KPI.
                We'll come back with a tailored campaign concept, for free.
              </p>
              <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  Free 20-minute strategy call
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  Multi-DMA targeting recommendations
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  Campaign-concept direction included
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                  Reporting cadence you can show your board
                </li>
              </ul>
            </div>
          )}
          <LeadForm trade={category.slug} tradeName={category.name} />
        </div>
      </section>

      {/* RELATED CATEGORIES */}
      <section
        className="px-5 sm:px-8 py-20 max-w-7xl mx-auto border-t border-[var(--af-line)]"
        data-testid="category-related"
      >
        <div className="eyebrow mb-3">Other Tourism Categories</div>
        <h3 className="font-display text-3xl sm:text-4xl mb-8">
          Run a multi-property portfolio? We do that too.
        </h3>
        <div className="grid sm:grid-cols-3 gap-5">
          {otherCategories.map((c) => {
            const OI = ICONS[c.icon] || Globe;
            return (
              <Link
                key={c.slug}
                to={`/tourism/${c.slug}`}
                className="surface p-5 flex items-center gap-4 group"
                data-testid={`category-related-${c.slug}`}
              >
                <div className="w-10 h-10 rounded-full bg-[var(--af-lime)]/15 border border-[var(--af-lime)]/40 flex items-center justify-center flex-shrink-0">
                  <OI size={18} className="text-[var(--af-lime)]" />
                </div>
                <div>
                  <div className="font-display text-lg leading-tight">
                    {c.name}
                  </div>
                  <div className="text-xs text-[var(--af-lime)] group-hover:underline underline-offset-4 mt-1">
                    View playbook →
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteShell>
  );
};

export default TourismCategoryPage;
